/**
 * ユーザー管理API サービス
 */

import api from './api'
import {
  User,
  UserDetail,
  UserListResponse,
  UserFilters,
  UserStats,
  BulkUpdateRequest,
  CSVImportOptions,
  CSVImportResponse,
} from '../types/user'

export class UserService {
  /**
   * ユーザー新規作成
   */
  static async createUser(userData: {
    name: string
    email: string
    department?: string
    position?: string
    role?: string
    password?: string
    isActive?: boolean
    preferredLanguage?: 'JA' | 'EN'
  }): Promise<User> {
    try {
      const response = await api.post<User>('/users', userData)
      return response
    } catch (error) {
      console.error('ユーザー作成エラー:', error)
      throw error
    }
  }

  /**
   * ユーザー一覧取得
   */
  static async getUsers(filters: UserFilters = {}): Promise<UserListResponse> {
    try {
      const params = new URLSearchParams()

      if (filters.page) params.append('page', filters.page.toString())
      if (filters.limit) params.append('limit', filters.limit.toString())
      if (filters.department) params.append('department', filters.department)
      if (filters.role) params.append('role', filters.role)
      if (filters.search) params.append('search', filters.search)
      if (filters.isActive !== undefined) {
        params.append('isActive', filters.isActive.toString())
      }

      const response = await api.get<UserListResponse>(`/users?${params.toString()}`)
      return response
    } catch (error) {
      console.error('ユーザー一覧取得エラー:', error)
      throw error
    }
  }

  /**
   * ユーザー詳細取得
   */
  static async getUser(userId: string): Promise<UserDetail> {
    try {
      const response = await api.get<UserDetail>(`/users/${userId}`)
      return response
    } catch (error) {
      console.error('ユーザー詳細取得エラー:', error)
      throw error
    }
  }

  /**
   * ユーザー統計情報取得
   */
  static async getUserStats(): Promise<UserStats> {
    try {
      const response = await api.get<UserStats>('/users/stats/overview')
      return response
    } catch (error) {
      console.error('ユーザー統計取得エラー:', error)
      throw error
    }
  }

  /**
   * プロフィール更新
   */
  static async updateProfile(
    userId: string,
    data: {
      name?: string
      email?: string
      department?: string | null
      position?: string | null
      role?: string
      currentPassword?: string
      newPassword?: string
    }
  ): Promise<User> {
    try {
      const response = await api.put<User>(`/users/${userId}`, data)
      return response
    } catch (error) {
      console.error('プロフィール更新エラー:', error)
      throw error
    }
  }

  /**
   * ユーザー一括更新
   */
  static async bulkUpdateUsers(
    data: BulkUpdateRequest
  ): Promise<{ message: string; updatedCount: number }> {
    try {
      const response = await api.put<{ message: string; updatedCount: number }>('/users/bulk', data)
      return response
    } catch (error) {
      console.error('ユーザー一括更新エラー:', error)
      throw error
    }
  }

  /**
   * ユーザー削除
   */
  static async deleteUser(userId: string): Promise<{ message: string }> {
    try {
      const response = await api.delete<{ message: string }>(`/users/${userId}`)
      return response
    } catch (error) {
      console.error('ユーザー削除エラー:', error)
      throw error
    }
  }

  /**
   * ユーザー一括ステータス変更
   */
  static async bulkUpdateStatus(
    userIds: string[],
    isActive: boolean
  ): Promise<{ message: string; updatedCount: number }> {
    try {
      const response = await api.put<{ message: string; updatedCount: number }>(
        '/users/bulk/status',
        {
          userIds,
          isActive,
        }
      )
      return response
    } catch (error) {
      console.error('ユーザー一括ステータス変更エラー:', error)
      throw error
    }
  }

  /**
   * ユーザー一括削除
   */
  static async bulkDeleteUsers(
    userIds: string[]
  ): Promise<{ message: string; deletedCount: number }> {
    try {
      const response = await api.delete<{ message: string; deletedCount: number }>('/users/bulk', {
        data: { userIds },
      })
      return response
    } catch (error) {
      console.error('ユーザー一括削除エラー:', error)
      throw error
    }
  }

  /**
   * CSV取り込み
   */
  static async importCSV(file: File, options: CSVImportOptions = {}): Promise<CSVImportResponse> {
    try {
      const formData = new FormData()
      formData.append('csvFile', file)

      if (options.updateExisting !== undefined) {
        formData.append('updateExisting', options.updateExisting.toString())
      }

      // CSV処理用の長いタイムアウト設定（60秒）
      const response = await api.upload<CSVImportResponse>('/users/import', formData, {
        timeout: 60000, // CSV処理は時間がかかるため60秒設定
      })
      return response
    } catch (error) {
      console.error('CSV取り込みエラー:', error)
      throw error
    }
  }

  /**
   * CSVテンプレートダウンロード
   */
  static async downloadCSVTemplate(): Promise<Blob> {
    try {
      const blob = await api.downloadBlob('/users/template')
      return blob
    } catch (error) {
      console.error('CSVテンプレートダウンロードエラー:', error)
      throw error
    }
  }

  /**
   * CSVテンプレートダウンロード（ブラウザ保存）
   */
  static async downloadCSVTemplateToBrowser(): Promise<void> {
    try {
      const blob = await this.downloadCSVTemplate()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'user_import_template.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('CSVテンプレート保存エラー:', error)
      throw error
    }
  }

  /**
   * ユーザーフィルター用部門一覧取得
   */
  static async getDepartments(): Promise<string[]> {
    try {
      const stats = await this.getUserStats()
      return stats.departmentDistribution.map((dept) => dept.department)
    } catch (error) {
      console.error('部門一覧取得エラー:', error)
      return []
    }
  }

  /**
   * ページネーション計算
   */
  static calculatePagination(currentPage: number, totalPages: number, delta: number = 2) {
    const pages = []
    const rangeStart = Math.max(1, currentPage - delta)
    const rangeEnd = Math.min(totalPages, currentPage + delta)

    // 最初のページ
    if (rangeStart > 1) {
      pages.push(1)
      if (rangeStart > 2) {
        pages.push('...')
      }
    }

    // 現在ページ周辺
    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i)
    }

    // 最後のページ
    if (rangeEnd < totalPages) {
      if (rangeEnd < totalPages - 1) {
        pages.push('...')
      }
      pages.push(totalPages)
    }

    return pages
  }

  /**
   * ユーザーロール表示名取得
   */
  static getRoleLabel(role: string): string {
    const roleLabels: { [key: string]: string } = {
      SUPER_ADMIN: 'スーパー管理者',
      CORPORATE_ADMIN: '管理者',
      USER: 'ユーザー',
    }
    return roleLabels[role] || role
  }

  /**
   * ユーザーステータス表示名取得
   */
  static getStatusLabel(isActive: boolean): string {
    return isActive ? 'アクティブ' : '無効'
  }

  /**
   * チュートリアル完了状況取得
   */
  static async getTutorialStatus(): Promise<{ tutorialCompleted: Record<string, boolean> }> {
    try {
      const response = await api.get<{ tutorialCompleted: Record<string, boolean> }>(
        '/users/tutorial/status'
      )
      return response
    } catch (error) {
      console.error('チュートリアル状況取得エラー:', error)
      throw error
    }
  }

  /**
   * チュートリアル完了をマーク
   */
  static async completeTutorial(
    tutorialKey: string
  ): Promise<{ message: string; tutorialCompleted: Record<string, boolean> }> {
    try {
      const response = await api.post<{
        message: string
        tutorialCompleted: Record<string, boolean>
      }>('/users/tutorial/complete', {
        tutorialKey,
      })
      return response
    } catch (error) {
      console.error('チュートリアル完了マークエラー:', error)
      throw error
    }
  }
}

// インスタンスをエクスポート
export const userService = new UserService()
